import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Entry,
  EntryReceipt,
  getEntryStatusLabel,
  ENTRY_STATUS_COLORS,
  getReceiptStatusLabel,
  RECEIPT_STATUS_COLORS,
} from '../../../models/Entry';
import { formatDate } from '@shared/utils/FormatUtils';
import { ChevronDown } from 'lucide-react';

interface EntryInfoProps {
  entry: Entry;
  receipt: EntryReceipt;
}

export const EntryInfo: React.FC<EntryInfoProps> = ({ entry, receipt }) => {
  const { t } = useTranslation('merchandiseV3');
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200/50 dark:border-blue-800/30 rounded-xl shadow-sm overflow-hidden">
      {/* Header compacto - siempre visible */}
      <div className="p-4">
        <div className="flex items-center gap-4">
          {/* Icono */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
          </div>

          {/* Información principal */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-bold text-foreground">{entry.reference}</h3>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${ENTRY_STATUS_COLORS[entry.status]}`}>
                {getEntryStatusLabel(entry.status, (key) => t(`status.${key.toLowerCase()}`, entry.status))}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${RECEIPT_STATUS_COLORS[receipt.status]}`}>
                {getReceiptStatusLabel(receipt.status, (key) =>
                  t(`receipt.status.${key.toLowerCase()}`, receipt.status),
                )}
              </span>
              {entry.team?.name && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  {entry.team.name}
                </span>
              )}
              <span className="font-mono text-sm text-muted-foreground">{receipt.code}</span>
            </div>
          </div>

          {/* Botón expandir/colapsar */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0 p-1 rounded-md hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors"
            aria-label={
              isExpanded ? t('receive.entry_info.collapse', 'Colapsar') : t('receive.entry_info.expand', 'Expandir')
            }
          >
            <ChevronDown
              className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Contenido expandible */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-blue-200/50 dark:border-blue-800/30 pt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 text-sm">
            {/* Información de entrada */}
            {entry.estimated_delivery_at && (
              <div>
                <span className="text-xs text-muted-foreground block mb-1">
                  {t('receive.entry_info.estimated_delivery', 'Fecha estimada')}
                </span>
                <span className="text-foreground">{formatDate(entry.estimated_delivery_at)}</span>
              </div>
            )}
            {entry.boxes_count && (
              <div>
                <span className="text-xs text-muted-foreground block mb-1">
                  {t('receive.entry_info.boxes', 'Cajas')}
                </span>
                <span className="text-foreground">{entry.boxes_count}</span>
              </div>
            )}
            {entry.tracking_code && (
              <div>
                <span className="text-xs text-muted-foreground block mb-1">
                  {t('receive.entry_info.tracking', 'Tracking')}
                </span>
                <span className="font-mono text-foreground">{entry.tracking_code}</span>
              </div>
            )}

            {/* Información de recepción */}
            {receipt.deadline_at && (
              <div>
                <span className="text-xs text-muted-foreground block mb-1">
                  {t('receive.entry_info.deadline', 'Límite')}
                </span>
                <span className="text-foreground">{formatDate(receipt.deadline_at)}</span>
              </div>
            )}
            {receipt.requirements_result && (
              <div>
                <span className="text-xs text-muted-foreground block mb-1">
                  {t('receive.entry_info.requirements_result', 'Requisitos')}
                </span>
                <span
                  className={`font-medium ${
                    receipt.requirements_result === 'INCUMPLE'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}
                >
                  {receipt.requirements_result === 'INCUMPLE'
                    ? t('receive.entry_info.non_compliance', 'Incumple')
                    : t('receive.entry_info.compliant', 'Correcta')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
